// PersonaDrawer.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Drawer,
    Box,
    Stack,
    Typography,
    Avatar,
    IconButton,
    List,
    ListItem,
    Divider,
    Button,
    Tooltip,
    Fab,
    Autocomplete,
    TextField,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Menu,
    MenuItem,
    ListItemIcon,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircleIcon from "@mui/icons-material/Circle";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AddIcon from "@mui/icons-material/Add";
import SortIcon from "@mui/icons-material/Sort";
import SearchIcon from "@mui/icons-material/Search";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Fuse from "fuse.js";

import { supabase } from "../../supabaseClient";
import { zIndexModals } from "./helpers";
import GroupChip from "./GroupChip";
import PersonaNotasSection from "./PersonaNotasSection";

const ask = (msg, def = "") => window.prompt(msg, def);
const ok = (msg) => window.confirm(msg);

const isTrue = (v) => v === true || v === "true" || v === 1 || v === "1";

const ArrowText = ({ text }) => (
    <Typography variant="body2" sx={{ mx: 0.5, fontFamily: "monospace" }}>
        {text}
    </Typography>
);

const PersonaDrawer = ({
    open,
    onClose,
    personaId,
    personas = [],
    relaciones = [],
    onChanged,
    grupos = [],
    gruposPersonas = [],
}) => {
    const persona = useMemo(
        () =>
            personas.find((p) => String(p.persona_id) === String(personaId)) ||
            null,
        [personas, personaId]
    );

    // --- Groups section helpers ---
    const gruposById = useMemo(() => {
        // Map<grupo_id string, full group object>
        const m = new Map();
        (grupos || []).forEach((g) => m.set(String(g.grupo_id), g));
        return m;
    }, [grupos]);

    const gruposDePersona = useMemo(() => {
        // comment: list of group objects the current person belongs to, with principal flag
        const pid = String(personaId || "");
        return (gruposPersonas || [])
            .filter((gp) => String(gp.persona_id) === pid)
            .map((gp) => {
                const g = gruposById.get(String(gp.grupo_id));
                if (!g) return null;
                return {
                    ...g,
                    grupo_principal: isTrue(gp.grupo_principal),
                };
            })
            .filter(Boolean);
    }, [gruposPersonas, gruposById, personaId]);

    const gruposDisponibles = useMemo(() => {
        const currentIds = new Set(
            gruposDePersona.map((g) => String(g.grupo_id))
        );
        return (grupos || []).filter(
            (g) => !currentIds.has(String(g.grupo_id))
        );
    }, [grupos, gruposDePersona]);

    const [selectedGrupo, setSelectedGrupo] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleAddToGroup = async () => {
        if (!persona) return;
        if (!selectedGrupo?.grupo_id) return;
        try {
            setSaving(true);
            const payload = {
                persona_id: persona.persona_id,
                grupo_id: selectedGrupo.grupo_id,
            };
            const { error } = await supabase
                .from("GruposPersonas")
                .insert(payload);
            if (error) throw error;
            setSelectedGrupo(null);
            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo añadir al grupo.");
        } finally {
            setSaving(false);
        }
    };

    // comment: remove person from given group
    const handleRemoveFromGroup = async (grupoId) => {
        if (!persona) return;
        if (!window.confirm("¿Quitar a la persona de este grupo?")) return;
        try {
            setSaving(true);
            const { error } = await supabase
                .from("GruposPersonas")
                .delete()
                .match({ persona_id: persona.persona_id, grupo_id: grupoId });
            if (error) throw error;
            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo quitar del grupo.");
        } finally {
            setSaving(false);
        }
    };

    // comment: toggle grupo_principal ensuring there is at most one
    const handleTogglePrincipalGroup = async (grupoId, alreadyPrincipal) => {
        if (!persona) return;
        try {
            setSaving(true);

            // comment: first clear principal flag for all groups of this person
            const { error: errClear } = await supabase
                .from("GruposPersonas")
                .update({ grupo_principal: false })
                .eq("persona_id", persona.persona_id);
            if (errClear) throw errClear;

            // comment: if clicked group was not principal, set it as principal
            if (!alreadyPrincipal) {
                const { error: errSet } = await supabase
                    .from("GruposPersonas")
                    .update({ grupo_principal: true })
                    .match({
                        persona_id: persona.persona_id,
                        grupo_id: grupoId,
                    });
                if (errSet) throw errSet;
            }

            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo actualizar el grupo principal.");
        } finally {
            setSaving(false);
        }
    };
    // --- End groups section helpers ---

    // useEffect(() => {
    //     console.log({ gruposDePersona, gruposById });
    // }, [gruposDePersona, gruposById]);

    const rows = useMemo(() => {
        const chosenId = String(personaId || "");
        const byId = Object.fromEntries(
            personas.map((p) => [String(p.persona_id), p])
        );
        return (relaciones || [])
            .map((r) => {
                const a = String(r.persona_a);
                const b = String(r.persona_b);
                const isChosenLeft = a === chosenId;
                const involves = isChosenLeft || b === chosenId;
                if (!involves) return null;

                const otherId = isChosenLeft ? b : a;
                const other = byId[otherId] || {};

                // comment: keep explicit aObj/bObj so visual order is A->B
                return {
                    key: `${a}__${b}__${r.tipo}__${r.reciproca ? 1 : 0}`,
                    isChosenLeft,
                    self: byId[chosenId] || {},
                    other,
                    persona_a: a,
                    persona_b: b,
                    aObj: byId[a] || {},
                    bObj: byId[b] || {},
                    tipo: r.tipo || "",
                    reciproca: !!r.reciproca,
                    relacion_principal: isTrue(r.relacion_principal),
                };
            })
            .filter(Boolean);
    }, [relaciones, personas, personaId]);

    const handleRenamePersona = async () => {
        if (!persona) return;
        const newName = ask(
            "Nuevo nombre visible para la persona:",
            persona.display_name || ""
        );
        if (!newName || newName.trim() === (persona.display_name || "").trim())
            return;
        try {
            setSaving(true);
            const { error } = await supabase
                .from("Persona")
                .update({ display_name: newName.trim() })
                .eq("persona_id", persona.persona_id);
            if (error) throw error;
            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo renombrar.");
        } finally {
            setSaving(false);
        }
    };

    // edit relation 'tipo' (simple prompt)
    const handleEditRelation = async (row) => {
        const newTipo = ask("Nuevo nombre para la relación:", row.tipo);
        if (newTipo === null) return;
        const trimmed = (newTipo || "").trim();
        if (!trimmed || trimmed === row.tipo) return;

        try {
            setSaving(true);
            const { error } = await supabase
                .from("RelacionesPersonas")
                .update({ tipo: trimmed })
                .match({
                    persona_a: row.persona_a,
                    persona_b: row.persona_b,
                    tipo: row.tipo,
                    reciproca: row.reciproca,
                });
            if (error) throw error;
            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo editar la relación.");
        } finally {
            setSaving(false);
        }
    };

    // delete a concrete relation
    const handleDeleteRelation = async (row) => {
        if (!ok(`¿Eliminar la relación "${row.tipo}"?`)) return;
        try {
            setSaving(true);
            const { error } = await supabase
                .from("RelacionesPersonas")
                .delete()
                .match({
                    persona_a: row.persona_a,
                    persona_b: row.persona_b,
                    tipo: row.tipo,
                    reciproca: row.reciproca,
                });
            if (error) throw error;
            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo eliminar la relación.");
        } finally {
            setSaving(false);
        }
    };

    // comment: toggle relacion_principal ensuring there is at most one per persona
    const handleTogglePrincipalRelation = async (row) => {
        if (!persona) return;
        try {
            setSaving(true);

            if (!row.relacion_principal) {
                // comment: clear principal flag for all relations involving this person
                const { error: errClear } = await supabase
                    .from("RelacionesPersonas")
                    .update({ relacion_principal: false })
                    .or(
                        `persona_a.eq.${persona.persona_id},persona_b.eq.${persona.persona_id}`
                    );
                if (errClear) throw errClear;

                // comment: set current relation as principal
                const { error: errSet } = await supabase
                    .from("RelacionesPersonas")
                    .update({ relacion_principal: true })
                    .match({
                        persona_a: row.persona_a,
                        persona_b: row.persona_b,
                        tipo: row.tipo,
                        reciproca: row.reciproca,
                    });
                if (errSet) throw errSet;
            } else {
                // comment: if it was already principal, just unset it
                const { error } = await supabase
                    .from("RelacionesPersonas")
                    .update({ relacion_principal: false })
                    .match({
                        persona_a: row.persona_a,
                        persona_b: row.persona_b,
                        tipo: row.tipo,
                        reciproca: row.reciproca,
                    });
                if (error) throw error;
            }

            if (onChanged) await onChanged();
        } catch (e) {
            alert(e?.message || "No se pudo actualizar la relación principal.");
        } finally {
            setSaving(false);
        }
    };

    // delete person with relations cleanup
    const handleDeletePersona = async () => {
        if (!persona) return;
        if (
            !ok(
                `Vas a borrar a "${persona.display_name}". Esto eliminará también sus relaciones. ¿Seguro?`
            )
        )
            return;
        try {
            setSaving(true);
            const { error: relErr } = await supabase
                .from("RelacionesPersonas")
                .delete()
                .or(
                    `persona_a.eq.${persona.persona_id},persona_b.eq.${persona.persona_id}`
                );
            if (relErr) throw relErr;

            const { error: perErr } = await supabase
                .from("Persona")
                .delete()
                .eq("persona_id", persona.persona_id);
            if (perErr) throw perErr;

            if (onChanged) await onChanged();
            onClose && onClose();
        } catch (e) {
            alert(e?.message || "No se pudo borrar la persona.");
        } finally {
            setSaving(false);
        }
    };

    if (!persona) {
        return (
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: { width: 420, maxWidth: "92vw", zIndex: zIndexModals },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Sin persona seleccionada.
                    </Typography>
                </Box>
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: 420, maxWidth: "92vw", zIndex: zIndexModals },
            }}
        >
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title={persona.display_name || "Sin nombre"} arrow>
                    <Avatar
                        alt={persona.display_name}
                        src={persona.googlePicURL || undefined}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                        sx={{ cursor: "default" }}
                    />
                </Tooltip>
                <Typography variant="h6">
                    {persona.display_name || "Sin nombre"}
                </Typography>
                <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                    <Fab
                        size="small"
                        color="default"
                        onClick={() => handleRenamePersona()}
                        aria-label="edit"
                    >
                        <EditIcon />
                    </Fab>
                    <Fab
                        size="small"
                        color="default"
                        onClick={() => onClose()}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </Fab>
                </Box>
            </Box>

            <Divider sx={{ my: 0 }} />

            {/* --- Groups section  --- */}
            <Box sx={{ px: 2, pb: 1, pt: 2 }}>
                <Typography variant="subtitle">Grupos</Typography>
            </Box>

            <Box sx={{ px: 2, pb: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {gruposDePersona.length > 0 ? (
                        gruposDePersona.map((g) => {
                            const isPrincipal = !!g.grupo_principal;

                            return (
                                <Tooltip
                                    key={String(g.grupo_id)}
                                    title={
                                        isPrincipal
                                            ? "Grupo principal"
                                            : "Marcar como grupo principal"
                                    }
                                    arrow
                                >
                                    <span>
                                        <GroupChip
                                            nombre={g?.nombre || "Sin nombre"}
                                            icon={g?.icon || ""}
                                            color={g?.color || "#999999"}
                                            deletable
                                            onDelete={() =>
                                                handleRemoveFromGroup(
                                                    g.grupo_id
                                                )
                                            }
                                            disabled={saving}
                                            variant="filled"
                                            size="small"
                                            // comment: star inside the chip, only in this panel
                                            principal={isPrincipal}
                                            showPrincipalIcon
                                            // comment: clicking the chip toggles principal
                                            onClick={() =>
                                                handleTogglePrincipalGroup(
                                                    g.grupo_id,
                                                    isPrincipal
                                                )
                                            }
                                        />
                                    </span>
                                </Tooltip>
                            );
                        })
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Sin grupos.
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
                {/* comment: autocomplete to pick an existing group to add */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <Autocomplete
                        value={selectedGrupo}
                        onChange={(_, val) => setSelectedGrupo(val)}
                        options={gruposDisponibles}
                        getOptionLabel={(opt) => opt?.nombre || ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Añadir a grupo"
                                size="small"
                            />
                        )}
                        sx={{ flex: 1, minWidth: 160 }}
                        disabled={saving || gruposDisponibles.length === 0}
                        isOptionEqualToValue={(o, v) =>
                            String(o.grupo_id) === String(v?.grupo_id)
                        }
                    />
                    <Button
                        variant="contained"
                        onClick={handleAddToGroup}
                        disabled={saving || !selectedGrupo}
                    >
                        Añadir
                    </Button>
                </Stack>
                {gruposDisponibles.length === 0 && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                    >
                        No hay grupos disponibles para añadir.
                    </Typography>
                )}
            </Box>
            {/* --- End groups section  --- */}

            <Divider sx={{ my: 0 }} />

            <Box sx={{ px: 2, pb: 1, pt: 2 }}>
                <Typography variant="subtitle">Relaciones</Typography>
            </Box>

            <List dense sx={{ px: 1 }}>
                {rows.map((row) => {
                    const left = row.aObj;
                    const right = row.bObj;
                    const arrow = row.reciproca ? "↔" : "→";
                    const isSelfLeft =
                        String(persona.persona_id) === row.persona_a;
                    const isSelfRight =
                        String(persona.persona_id) === row.persona_b;

                    return (
                        <ListItem
                            key={row.key}
                            secondaryAction={
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip
                                        title={
                                            row.relacion_principal
                                                ? "Relación principal"
                                                : "Marcar como relación principal"
                                        }
                                        arrow
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleTogglePrincipalRelation(
                                                        row
                                                    )
                                                }
                                                aria-label="principal-relation"
                                                disabled={saving}
                                            >
                                                {row.relacion_principal ? (
                                                    <StarIcon fontSize="small" />
                                                ) : (
                                                    <StarBorderIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip
                                        title="Editar nombre de la relación"
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleEditRelation(row)
                                            }
                                            aria-label="edit"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar relación" arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleDeleteRelation(row)
                                            }
                                            aria-label="delete"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        >
                            {/* comment: visual: [A Avatar]  <arrow>  [B Avatar]  |  relation name */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ flex: 1, minWidth: 0 }}
                            >
                                <Tooltip
                                    title={left?.display_name || "Sin nombre"}
                                    arrow
                                >
                                    <Avatar
                                        alt={left?.display_name}
                                        src={left?.googlePicURL || undefined}
                                        imgProps={{
                                            referrerPolicy: "no-referrer",
                                        }}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            outline: isSelfLeft
                                                ? "2px solid"
                                                : "none",
                                            outlineColor: isSelfLeft
                                                ? "primary.main"
                                                : "transparent",
                                        }}
                                    >
                                        {(left?.display_name || "?")
                                            .charAt(0)
                                            .toUpperCase()}
                                    </Avatar>
                                </Tooltip>

                                <ArrowText text={arrow} />

                                <Tooltip
                                    title={right?.display_name || "Sin nombre"}
                                    arrow
                                >
                                    <Avatar
                                        alt={right?.display_name}
                                        src={right?.googlePicURL || undefined}
                                        imgProps={{
                                            referrerPolicy: "no-referrer",
                                        }}
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            outline: isSelfRight
                                                ? "2px solid"
                                                : "none",
                                            outlineColor: isSelfRight
                                                ? "primary.main"
                                                : "transparent",
                                        }}
                                    >
                                        {(right?.display_name || "?")
                                            .charAt(0)
                                            .toUpperCase()}
                                    </Avatar>
                                </Tooltip>

                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    sx={{
                                        ml: 1,
                                        minWidth: 80,
                                        textAlign: "left",
                                    }}
                                    title={row.tipo}
                                >
                                    {row.tipo}
                                </Typography>
                            </Stack>
                        </ListItem>
                    );
                })}
                {!rows.length && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Sin relaciones.
                        </Typography>
                    </Box>
                )}
            </List>

            <Divider sx={{ my: 0 }} />

            {/* --- Notes section --- */}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <PersonaNotasSection
                    personaId={personaId}
                    persona={persona}
                    open={open}
                    personas={personas}
                />
            </Box>

            <Divider sx={{ mt: 1, mb: 2 }} />

            {/* Delete button stays at the bottom */}
            <Box sx={{ px: 2, pb: 2 }}>
                <Button
                    color="error"
                    variant="outlined"
                    onClick={handleDeletePersona}
                    disabled={saving}
                >
                    Borrar persona
                </Button>
            </Box>
        </Drawer>
    );
};

export default PersonaDrawer;
